/**
 * sso.ts — federated sign-in (Google, Microsoft) through the Cognito Hosted UI.
 * Adapted from enforza-neon's console.
 *
 * OAuth 2.0 Authorization Code flow with PKCE (no client secret):
 *   1. beginSsoSignIn() stores a PKCE verifier + random state in sessionStorage and
 *      redirects to https://<cognito-domain>/oauth2/authorize?identity_provider=Google…
 *   2. Cognito sends the user to the provider, then back to our /auth/callback
 *      with ?code=…&state=…
 *   3. AuthCallbackPage calls exchangeSsoCode(), which checks state and swaps the
 *      code + verifier for Cognito tokens at /oauth2/token.
 */
import { config } from "../config";
import type { AuthTokens } from "./cognito";

// Identity-provider names as configured on the Cognito user pool
export type SsoProvider = "Google" | "Microsoft";

/** Providers this build is configured for (none unless the Hosted UI domain is set). */
export function enabledSsoProviders(): SsoProvider[] {
  if (!config.cognito.domain) return [];
  return config.cognito.ssoProviders.filter((p): p is SsoProvider => p === "Google" || p === "Microsoft");
}

/** Must exactly match a callback URL registered on the Cognito app client. */
function callbackUri(): string {
  return `${window.location.origin}/auth/callback`;
}

// ─── PKCE helpers (RFC 7636) ────────────────────────────────────────────────

function base64UrlEncode(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(byteCount = 32): string {
  const buf = new Uint8Array(byteCount);
  crypto.getRandomValues(buf);
  return base64UrlEncode(buf);
}

async function sha256Base64Url(input: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return base64UrlEncode(new Uint8Array(digest));
}

// sessionStorage is per-tab, so parallel sign-ins in two tabs don't collide
const SS_VERIFIER = "xsc.sso.verifier";
const SS_STATE = "xsc.sso.state";
const SS_NEXT = "xsc.sso.next";

// ─── Public API ─────────────────────────────────────────────────────────────

/** Start a federated sign-in. Navigates away from the page. */
export async function beginSsoSignIn(provider: SsoProvider, next?: string): Promise<void> {
  if (!config.cognito.domain) throw new Error("Single sign-on isn't set up for this environment yet.");

  const verifier = randomString();
  const state = randomString(16);
  sessionStorage.setItem(SS_VERIFIER, verifier);
  sessionStorage.setItem(SS_STATE, state);
  if (next) sessionStorage.setItem(SS_NEXT, next);

  const params = new URLSearchParams({
    client_id: config.cognito.clientId,
    response_type: "code",
    scope: "openid email profile",
    redirect_uri: callbackUri(),
    identity_provider: provider,
    state,
    code_challenge: await sha256Base64Url(verifier),
    code_challenge_method: "S256",
    // Always show the provider's account chooser rather than silently reusing the last account
    prompt: "select_account",
  });
  window.location.assign(`https://${config.cognito.domain}/oauth2/authorize?${params}`);
}

/**
 * Exchange the callback's code for tokens. Returns the tokens and the page the
 * user was heading to. Throws if the state doesn't match or the exchange fails.
 */
export async function exchangeSsoCode(code: string, state: string): Promise<{ tokens: AuthTokens; next: string }> {
  const expectedState = sessionStorage.getItem(SS_STATE);
  const verifier = sessionStorage.getItem(SS_VERIFIER);
  const next = sessionStorage.getItem(SS_NEXT) ?? "/";
  // One-shot values: clear them whatever happens
  sessionStorage.removeItem(SS_STATE);
  sessionStorage.removeItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_NEXT);

  if (!expectedState || state !== expectedState) throw new Error("Sign-in link expired or was tampered with. Please try again.");
  if (!verifier) throw new Error("Sign-in session was lost. Please try again.");

  const res = await fetch(`https://${config.cognito.domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.cognito.clientId,
      code,
      redirect_uri: callbackUri(),
      code_verifier: verifier,
    }),
  });
  if (!res.ok) throw new Error("Single sign-on failed. Please try again.");
  const json = await res.json();
  return {
    tokens: {
      idToken: json.id_token,
      accessToken: json.access_token,
      refreshToken: json.refresh_token,
      expiresIn: json.expires_in,
    },
    next,
  };
}
